import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const TiltCard = ({ className = "", testId, children }) => {
    const rx = useMotionValue(0);
    const ry = useMotionValue(0);
    const srx = useSpring(rx, { stiffness: 140, damping: 18 });
    const sry = useSpring(ry, { stiffness: 140, damping: 18 });

    const handleMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ry.set(px * 7);
        rx.set(-py * 7);
    };
    const reset = () => {
        rx.set(0);
        ry.set(0);
    };

    return (
        <div style={{ perspective: 1200 }} className={className}>
            <motion.div
                data-testid={testId}
                onMouseMove={handleMove}
                onMouseLeave={reset}
                style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
                className="group relative h-full border border-white/10 bg-[#0c0c0c] p-8 transition-colors duration-500 hover:border-white/30 md:p-12"
            >
                {children}
            </motion.div>
        </div>
    );
};

const CardContent = ({ index, title, body, tags }) => (
    <div className="flex h-full flex-col justify-between gap-16">
        <div className="flex items-start justify-between">
            <span className="font-mono-x text-xs tracking-[0.2em] text-zinc-500">
                [{index}]
            </span>
            <ArrowUpRight className="h-6 w-6 text-zinc-600 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-white" />
        </div>
        <div>
            <h3 className="font-display text-3xl font-bold tracking-tight text-white md:text-4xl">
                {title}
            </h3>
            <p className="mt-4 max-w-md text-base leading-relaxed text-zinc-400">{body}</p>
            <div className="mt-8 flex flex-wrap gap-2">
                {tags.map((tag) => (
                    <span
                        key={tag}
                        className="rounded-full border border-white/10 px-3 py-1 font-mono-x text-[10px] uppercase tracking-[0.15em] text-zinc-500"
                    >
                        {tag}
                    </span>
                ))}
            </div>
        </div>
    </div>
);

export const Services = () => (
    <section
        id="services"
        data-testid="services-section"
        className="px-6 py-24 md:px-12 md:py-32"
    >
        <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="mb-16 md:mb-24"
        >
            <p className="font-mono-x mb-6 text-xs uppercase tracking-[0.25em] text-zinc-500">
                [ Services ]
            </p>
            <h2 className="font-display max-w-3xl text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
                Everything after the idea.
            </h2>
        </motion.div>

        <motion.div
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: EASE }}
            className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8"
        >
            <TiltCard className="md:col-span-7" testId="service-card-web">
                <CardContent
                    index="01"
                    title="Website Design & Development"
                    body="Conversion-obsessed websites and web apps, designed and engineered in-house. No templates, no page builders — a product-grade build that loads fast and sells hard."
                    tags={["UI/UX", "Development", "SEO", "Analytics"]}
                />
            </TiltCard>
            <TiltCard className="md:col-span-5" testId="service-card-marketing">
                <CardContent
                    index="02"
                    title="Digital Marketing"
                    body="Full-funnel campaigns that turn attention into revenue — paid, organic, and email, with every rupee and click measured."
                    tags={["Funnels", "Paid Ads", "Content", "Email"]}
                />
            </TiltCard>
            <TiltCard className="md:col-span-5" testId="service-card-automation">
                <CardContent
                    index="03"
                    title="Business Streamlining"
                    body="Automations that reply to leads in seconds, onboard clients while you sleep, and kill the copy-paste work eating your week."
                    tags={["Lead Routing", "CRM", "Onboarding", "Workflows"]}
                />
            </TiltCard>
            <TiltCard className="md:col-span-7" testId="service-card-studio">
                <div className="spotlight-frame relative flex h-full min-h-[280px] flex-col justify-end">
                    <img
                        src="https://images.pexels.com/photos/30547584/pexels-photo-30547584.jpeg?auto=compress&cs=tinysrgb&w=1600"
                        alt="Abstract digital landscape representing streamlined systems"
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover opacity-50 transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                    <p className="font-display relative z-20 max-w-md text-2xl font-bold tracking-tight text-white md:text-3xl">
                        One team. First sketch to first sale. Zero handoffs.
                    </p>
                </div>
            </TiltCard>
        </motion.div>
    </section>
);
